/**
 * Turns one audit row into business English.
 *
 * The audit endpoint records HTTP: `PATCH /api/v1/deals/67 · 200 · 12ms`. That
 * is the right thing to STORE and the wrong thing to READ — the people who open
 * this screen are checking who changed a deal, not tuning an endpoint. This
 * module is the single translation layer between the two registers, kept pure
 * and free of React so the list, the detail drawer and any future digest all
 * phrase the same row identically.
 *
 * Two rules govern everything below, and they beat readability when they
 * conflict with it:
 *
 *  1. **Never invent a fact.** Where the log is imprecise — a null `recordId`
 *     on a nested route, a module slug this build has never heard of — the
 *     sentence gets vaguer, never more confident.
 *  2. **Never claim a non-2xx did anything.** A 403 is an ATTEMPT. Phrasing it
 *     "deleted Quote #3" would make the audit trail actively lie, which is far
 *     worse than making it read like a server log.
 *  3. **Never attribute a support session to one person.** A super admin acting
 *     as another account produces a row that must name BOTH — "Moin Mhatarnaik,
 *     acting as Anvita Shetty, updated Company #12" — because the target alone
 *     is not an imprecise attribution but a false one, and it is false in the
 *     direction that settles disputes wrongly. See `supportSessionActorPhrase`.
 *
 * Nothing here throws. It renders a whole page, and one malformed row must not
 * take that page down, so every field has a fallback.
 */
import { ACTION_SLUGS, MODULE_SLUGS, actorLabel, rowActorState } from '@/lib/api-log-utils';
import type { ActorState, SimpleActorState } from '@/lib/api-log-utils';
import type { ApiLogSummary } from '@/lib/types';

/*
 * `statusTone` and `formatLatency` are deliberately NOT imported. This module
 * produces words; the badge tone and the latency string are presentation
 * concerns the row component already gets straight from `api-log-utils`, and
 * re-exporting them through here would give the app two import paths for one
 * helper.
 */

/**
 * What became of the request, in business terms rather than status codes.
 *
 * Separate from the raw `statusCode` because the feed branches on it in three
 * places — the phrasing of the sentence, the label beside it, and the filter
 * chips — and each of those matching on numeric ranges independently is how one
 * of them ends up disagreeing with the other two.
 */
export type ActivityOutcome =
  | 'succeeded'
  | 'denied'
  | 'not-found'
  | 'invalid'
  | 'rate-limited'
  | 'failed'
  | 'unauthenticated';

/**
 * One audit row, restated as an activity.
 *
 * Both the parts and the assembled `sentence` are returned: the feed renders
 * the sentence, while the detail drawer and any grouping UI want the pieces
 * separately. They are built together so the two can never drift apart.
 */
export interface ActivityDescription {
  /**
   * "Varun Khubani" | "Someone not signed in" | "A deleted account (a@b.com)".
   *
   * A support session names both people and KEEPS ITS TRAILING COMMA —
   * "Moin Mhatarnaik, acting as Anvita Shetty," — because the clause has to be
   * closed before the verb. `sentence` always starts with this exact string, so
   * a renderer can still lift it off the front as the prefix.
   */
  actor: string;
  /** Past-tense verb phrase: "updated", "created", "deleted", "converted", "signed in", "viewed". */
  verb: string;
  /** "Deal #67" | "the deals list" | "Permission set #3" | null when nothing sensible. */
  object: string | null;
  /** The whole thing as one readable sentence, actor included. Never ends with a full stop. */
  sentence: string;
  outcome: ActivityOutcome;
  /** "succeeded" | "not permitted" | "record not found" | … — plain words, lowercase. */
  outcomeLabel: string;
  /** In-app route to the record, e.g. "/deals/67". Null when there is none. */
  href: string | null;
  /** True for background noise — health checks, token refreshes, permission lookups. */
  routine: boolean;
  /**
   * True when this happened inside a support session — a super admin acting as
   * another account.
   *
   * `actor` and `sentence` already name both people, so this is not what keeps
   * the promise; it is here so the UI can add a badge as a SECOND signal beside
   * the words, and so nothing has to re-derive the fact by pattern-matching the
   * sentence for ", acting as ". It is also what forces `routine` to false.
   */
  supportSession: boolean;
}

/* ------------------------------------------------------------------ *
 * Vocabulary tables
 * ------------------------------------------------------------------ */

/**
 * The three forms of a verb this module needs.
 *
 * `past` is what a 2xx did; `infinitive` is what a non-2xx merely tried to do.
 * Storing both beats deriving one from the other — "signed in" -> "sign in" and
 * "renewed their session" -> "renew their session" are not a suffix rule, and a
 * regex that guessed would eventually emit "tried to signed in".
 */
interface VerbForms {
  /** The value exposed as `ActivityDescription.verb`. */
  label: string;
  /** Slots after the actor in a completed sentence: "Varun Khubani <past> …". */
  past: string;
  /** Slots after "tried to" in an attempt sentence: "… tried to <infinitive> …". */
  infinitive: string;
}

/**
 * Permission action slugs, which are the audit's own vocabulary rather than
 * HTTP verbs — 'convert' and 'export' have no method that implies them, so the
 * action is always preferred over the method when present.
 */
const ACTION_VERBS: Record<string, VerbForms> = {
  create: { label: 'created', past: 'created', infinitive: 'create' },
  read: { label: 'viewed', past: 'viewed', infinitive: 'view' },
  update: { label: 'updated', past: 'updated', infinitive: 'update' },
  delete: { label: 'deleted', past: 'deleted', infinitive: 'delete' },
  convert: { label: 'converted', past: 'converted', infinitive: 'convert' },
  export: { label: 'exported', past: 'exported', infinitive: 'export' },
};

/**
 * The method-only fallback, for rows outside the permission system where
 * `action` is null. Weaker than the action map — a POST is not always a create
 * — but "created" beats "sent a POST" for a reader, and the special-cased
 * routes below intercept the cases where it would read as nonsense.
 */
const METHOD_VERBS: Record<string, VerbForms> = {
  GET: { label: 'viewed', past: 'viewed', infinitive: 'view' },
  HEAD: { label: 'viewed', past: 'viewed', infinitive: 'view' },
  POST: { label: 'created', past: 'created', infinitive: 'create' },
  PUT: { label: 'updated', past: 'updated', infinitive: 'update' },
  PATCH: { label: 'updated', past: 'updated', infinitive: 'update' },
  DELETE: { label: 'deleted', past: 'deleted', infinitive: 'delete' },
};

/** Last resort. Says something happened without claiming what. */
const UNKNOWN_VERB: VerbForms = { label: 'accessed', past: 'accessed', infinitive: 'access' };

/**
 * How each module is named in prose.
 *
 * `collection` is spelled out per module rather than derived as
 * `the ${plural} list`, because two of them break that pattern: the audit log
 * is "the audit log", not "the audit logs list", and reference data is a mass
 * noun. Deriving would have produced both mistakes silently.
 */
interface ModuleNouns {
  /** Titlecase singular, paired with `#id`: "Deal #67". */
  singular: string;
  /** Titlecase plural, the value `moduleLabel` returns: "Deals", "Audit log". */
  plural: string;
  /** Lowercase phrase for a route with no record: "the deals list". */
  collection: string;
  /**
   * False when the slug was not in the contract's list and these nouns were
   * guessed from the slug text. Guessed nouns are never bent into an indefinite
   * singular ("a partner orgs"), because the singular is the part we don't have.
   */
  known: boolean;
}

const MODULE_NOUNS: Record<string, ModuleNouns> = {
  deals: { singular: 'Deal', plural: 'Deals', collection: 'the deals list', known: true },
  projects: {
    singular: 'Project',
    plural: 'Projects',
    collection: 'the projects list',
    known: true,
  },
  quotes: { singular: 'Quote', plural: 'Quotes', collection: 'the quotes list', known: true },
  invoices: {
    singular: 'Invoice',
    plural: 'Invoices',
    collection: 'the invoices list',
    known: true,
  },
  deliverables: {
    singular: 'Deliverable',
    plural: 'Deliverables',
    collection: 'the deliverables list',
    known: true,
  },
  companies: {
    singular: 'Company',
    plural: 'Companies',
    collection: 'the companies list',
    known: true,
  },
  contacts: {
    singular: 'Contact',
    plural: 'Contacts',
    collection: 'the contacts list',
    known: true,
  },
  products: {
    singular: 'Product',
    plural: 'Products',
    collection: 'the products list',
    known: true,
  },
  cycles: { singular: 'Cycle', plural: 'Cycles', collection: 'the cycles list', known: true },
  intakes: { singular: 'Intake', plural: 'Intakes', collection: 'the intakes list', known: true },
  users: { singular: 'User', plural: 'Users', collection: 'the users list', known: true },
  'reference-data': {
    singular: 'Reference record',
    plural: 'Reference data',
    collection: 'the reference data',
    known: true,
  },
  'api-logs': {
    singular: 'Audit log entry',
    plural: 'Audit log',
    collection: 'the audit log',
    known: true,
  },
};

/**
 * Modules whose records have a real detail route in `App.tsx`.
 *
 * Narrower than `MODULE_NOUNS` on purpose. `products`, `cycles`, `intakes` and
 * `users` are managed entirely from their index screens — there is no
 * `/products/:id` in the route table — so linking "Product #4" would land the
 * reader on the 404 page and make the feed look broken. `reference-data` and
 * `api-logs` have no per-record page either. When one of those gains a detail
 * route, add it here and nowhere else.
 */
const DETAIL_ROUTES: Record<string, string> = {
  deals: '/deals',
  quotes: '/quotes',
  invoices: '/invoices',
  /*
   * ⚠️ `deliverables` was here and is REMOVED — `/deliverables/:id` left the
   * route table when the old hand-entered entity was deleted. It stays in
   * `MODULE_NOUNS` above, so an audit-log row about a deliverable still reads
   * as a sentence; it simply no longer links, which is exactly what this map is
   * narrower than `MODULE_NOUNS` for.
   *
   * ⚠️ The trail keeps those rows for ever, so this is not an empty branch: the
   * module still appears in the feed, and leaving the entry here would have
   * pointed every one of those rows at the 404 page. Nothing would have failed
   * — a route string is invisible to `tsc` and to oxlint alike.
   */
  companies: '/companies',
  contacts: '/contacts',
  /*
   * Added the day `/projects/:id` was routed. Before that a link here would
   * have landed on the 404 page -- which is the whole reason this map is
   * narrower than MODULE_NOUNS.
   */
  projects: '/projects',
};

/**
 * Wording for each outcome.
 *
 * Lowercase and plain, because these are appended after a middot to a sentence
 * that already carries the weight — "tried to delete Quote #3 · not permitted".
 */
const OUTCOME_LABELS: Record<ActivityOutcome, string> = {
  succeeded: 'succeeded',
  /*
   * NOT "not signed in", which claims more than the row can support. The audit
   * trail cannot tell a request that carried no credential from one whose
   * credential was rejected: `authenticate` refuses before `req.user` is ever
   * set, so all 62 recorded 401s in production have a null actor either way.
   * "No valid sign-in" is true in both cases.
   */
  unauthenticated: 'no valid sign-in',
  denied: 'not permitted',
  'not-found': 'record not found',
  invalid: 'rejected as invalid',
  'rate-limited': 'too many requests',
  failed: 'failed',
};

/* ------------------------------------------------------------------ *
 * Defensive readers
 * ------------------------------------------------------------------ */

/** A trimmed string, or null for anything that is not usable text. */
function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Titlecases a slug this build has never seen.
 *
 * Deliberately does NOT try to singularise: there is no honest way to turn an
 * unknown `partner-orgs` into `Partner org`, and a guessed noun in an audit
 * trail is exactly the kind of small invention this module exists to avoid. The
 * plural stands in for the singular and reads slightly awkwardly, which is the
 * correct amount of awkward for a fact we do not have.
 */
function prettifySlug(slug: string): string {
  const words = slug.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Nouns for a module slug, falling back rather than failing.
 *
 * Membership of `MODULE_SLUGS` — the contract's own list — is checked first, so
 * a slug the backend adds tomorrow still renders as words instead of vanishing
 * from the feed.
 */
function nounsFor(slug: string): ModuleNouns {
  const known = MODULE_SLUGS.includes(slug) ? MODULE_NOUNS[slug] : undefined;
  if (known !== undefined) return known;

  const titled = prettifySlug(slug);
  return {
    singular: titled,
    plural: titled,
    collection: `the ${titled.toLowerCase()} list`,
    known: false,
  };
}

/**
 * The route pattern with any `/api/v<n>` prefix removed, for matching.
 *
 * Falls back to `path` (query string stripped) when `route` is null, which is
 * the normal state for a 404 — no Express route matched. That fallback is what
 * lets a request to a mistyped auth URL still be recognised as auth traffic
 * rather than collapsing to "accessed" with no object at all.
 */
function matchablePath(row: Partial<ApiLogSummary>): string {
  const raw = text(row.route) ?? text(text(row.path)?.split('?')[0]) ?? '';
  return raw.replace(/^\/api\/v\d+/, '').replace(/\/+$/, '') || '/';
}

/* ------------------------------------------------------------------ *
 * Exports
 * ------------------------------------------------------------------ */

/**
 * Human plural noun for a module slug: 'deals' -> 'Deals', 'api-logs' -> 'Audit log'.
 *
 * Returns null only for a genuinely absent module — the /auth, /health and
 * /permissions routes log `module: null`, and that is a real fact ("outside the
 * permission system"), not missing data, so the caller decides how to show it
 * rather than being handed a placeholder string.
 */
export function moduleLabel(slug: string | null): string | null {
  const value = text(slug);
  if (value === null) return null;
  return nounsFor(value).plural;
}

/**
 * Business-friendly label for an action slug: 'create' -> 'Created', 'read' -> 'Viewed'.
 *
 * 'read' becomes "Viewed" rather than "Read", which in a past-tense feed is
 * unreadably ambiguous with the present tense. Validated against `ACTION_SLUGS`
 * so an unrecognised value is titlecased and shown rather than silently mapped
 * onto a verb it does not mean.
 */
export function actionLabel(slug: string | null): string | null {
  const value = text(slug);
  if (value === null) return null;

  if (ACTION_SLUGS.includes(value)) {
    const verb = ACTION_VERBS[value];
    if (verb !== undefined) return verb.label.charAt(0).toUpperCase() + verb.label.slice(1);
  }
  return prettifySlug(value);
}

/**
 * Restates one audit row as a business activity.
 *
 * Pure, total, and never throws — `log` is read defensively field by field
 * because this runs once per row over a page of audit data, and a single row
 * with an unexpected shape must degrade to a vaguer sentence rather than blank
 * the screen.
 */
export function describeActivity(log: ApiLogSummary): ActivityDescription {
  const row: Partial<ApiLogSummary> = (log ?? {}) as Partial<ApiLogSummary>;

  const outcome = outcomeOf(row.statusCode);
  const succeeded = outcome === 'succeeded';

  /*
   * The support-session check runs FIRST, ahead of the 401 rule below it.
   *
   * A support session should never produce a 401 — the session cannot exist
   * without a valid sign-in — so the two are not expected to collide. They are
   * ordered anyway, because if they ever do, the marker has to win: replacing
   * two named people with "An unidentified caller" would drop the super admin
   * out of the record at the one moment somebody is reading it to find out who
   * was there.
   */
  const state = rowActorState(log);
  const supportSession = state.kind === 'support-session';

  /*
   * A 401's actor is ALWAYS null, and that null means nothing.
   *
   * `authenticate` rejects before it sets `req.user`, so the trail records no
   * actor whether the caller sent nothing at all or sent a token that was
   * expired, forged or revoked. Calling that "Someone not signed in" — the
   * honest phrase for an anonymous 2xx — asserts the first of those on a row
   * that cannot distinguish them, and a reader hunting a break-in attempt would
   * be misled by it in exactly the wrong direction.
   *
   * Only 401 gets this treatment. A null actor on any other status genuinely
   * does mean nobody was signed in.
   */
  const actor =
    !supportSession && outcome === 'unauthenticated' && row.actor == null
      ? 'An unidentified caller'
      : describeActor(state);
  const special = specialCase(matchablePath(row), row);

  const verb = special?.verb ?? verbFor(row);
  const object = special === null ? objectFor(row, verb) : special.object;

  /*
   * A request that matched NO route at all — `route: null` with no module and
   * no special case. Observed in production as "Someone not signed in tried to
   * create", which is a sentence with its object missing and tells the reader
   * nothing. It is almost always a mistyped or outdated URL, so say that
   * instead of guessing at an object that does not exist.
   */
  const unrouted = special === null && row.route == null && object === null;
  // A 404 is the server saying that record is not there. Offering a link to it
  // would hand the reader a guaranteed dead end.
  const href = special === null && outcome !== 'not-found' ? hrefFor(row) : null;
  /*
   * ⚠️ A SUPPORT SESSION IS NEVER ROUTINE, whatever its method or status.
   *
   * `isRoutine` hides every GET+read, which is the right default for ordinary
   * traffic and exactly wrong here: a super admin READING another person's
   * records is one of the things a support session is opened to do, and it is
   * one of the things a reader would come to this screen to check. Left to the
   * generic rule it would be collapsed into the "N routine entries hidden"
   * count and never seen.
   *
   * The same escape hatch the page already gives a failed sign-in, applied one
   * layer lower so every consumer of `describeActivity` inherits it rather than
   * each surface remembering to re-exempt it.
   */
  const routine = supportSession ? false : (special?.routine ?? isRoutine(row));

  return {
    actor,
    verb: verb.label,
    object,
    sentence: unrouted
      ? `${actor} requested an address that does not exist`
      : (special?.sentence?.(actor, succeeded) ??
        buildSentence(actor, verb, object, succeeded)),
    outcome,
    outcomeLabel: outcomeLabelOf(outcome, row.statusCode),
    href,
    routine,
    supportSession,
  };
}

/* ------------------------------------------------------------------ *
 * Internals
 * ------------------------------------------------------------------ */

/**
 * Names the actor in the register of a feed rather than a table cell.
 *
 * The deleted case is the whole reason this is not simply `actorLabel`. That
 * helper returns the bare email, which is correct for a column with a "deleted"
 * badge beside it; dropped into a sentence it reads as an ordinary address and
 * the reader never learns the account is gone. Here the marker is in the words
 * themselves — "A deleted account (x@y.com)" — because a sentence has nowhere
 * to hang a badge. It must never read as "Anonymous": the audit exists
 * precisely so a departed person's actions stay attributable.
 */
function describeActor(state: ActorState): string {
  if (state.kind === 'support-session') return supportSessionActorPhrase(state);

  switch (state.kind) {
    case 'anonymous':
      return ANONYMOUS_ACTOR;
    case 'user':
      return namedOr(state, 'An unidentified account');
    case 'deleted':
      return `A deleted account (${state.email})`;
    default:
      return 'An unidentified account';
  }
}

/**
 * A `'user'` state's display name, or `fallback` when it has neither.
 *
 * `actorState` accepts any non-null name, including `''` — a real value on
 * `KanbanOwner`-shaped records elsewhere in this app — so the name is tried
 * first, then the email, before anything is invented.
 */
function namedOr(state: Extract<SimpleActorState, { kind: 'user' }>, fallback: string): string {
  return text(actorLabel(state)) ?? text(state.email) ?? fallback;
}

/**
 * Names ONE PARTY of a support session.
 *
 * `leading` decides capitalisation and nothing else. The operator opens the
 * sentence and the target sits mid-clause after "acting as", so the same state
 * renders as "A deleted account (…)" in one slot and "a deleted account (…)" in
 * the other. Deriving that with a `toLowerCase()` on the first character would
 * work here and break the moment a real name is the leading word —
 * "moin mhatarnaik" — so the two forms are chosen, not computed.
 *
 * ⚠️ An unnameable party reads "an account this row does not name", NOT the
 * ordinary path's "An unidentified account" and never "Someone not signed in".
 * Both parties to a support session were signed in by definition — the session
 * could not otherwise have been opened — so the honest statement is about the
 * ROW's silence, not about the person's session. The wording matches
 * `partyLabel` in `api-log-utils` and the detail modal's `Party`, so the three
 * surfaces cannot describe the same gap three ways.
 */
function supportSessionParty(state: SimpleActorState, leading: boolean): string {
  const unnamed = leading
    ? 'An account this row does not name'
    : 'an account this row does not name';

  switch (state.kind) {
    case 'user':
      return namedOr(state, unnamed);
    case 'deleted':
      return `${leading ? 'A' : 'a'} deleted account (${state.email})`;
    default:
      return unnamed;
  }
}

/**
 * The whole actor phrase for a support session: BOTH people, in one string.
 *
 * Exported because it is the load-bearing sentence of this feature and the
 * repo has no test setup — it is the piece worth exercising by hand against a
 * table of cases, and a function that can only be reached through
 * `describeActivity` cannot be.
 *
 * Shape: `"<operator>, acting as <target>,"` — which drops straight into
 * `buildSentence` and inherits rule 2 unchanged, so a 2xx reads "Moin
 * Mhatarnaik, acting as Anvita Shetty, updated Company #12" and a 403 reads
 * "Moin Mhatarnaik, acting as Anvita Shetty, tried to delete Quote #3". The
 * attempt phrasing is not special-cased here precisely so it cannot drift from
 * the rule every other row follows.
 *
 * ⚠️ THE TRAILING COMMA IS PART OF THE VALUE, and it is not decoration. The
 * clause is an appositive and needs closing: `buildSentence` joins with a single
 * space, so without it the sentence reads "Moin Mhatarnaik, acting as Anvita
 * Shetty updated Company #12" — which parses as the target having done the
 * updating, the exact misattribution this function exists to prevent. It is here
 * rather than in `buildSentence` because only this phrase needs it; every other
 * actor is a bare noun phrase. `ApiLogsPage` bolds `description.actor` as the
 * sentence's prefix, so the comma is emphasised with the clause, which is right.
 *
 * Two wording decisions worth not re-litigating:
 *
 *  - The comma-bracketed clause, not "on behalf of" and not a parenthesis. The
 *    super admin is the grammatical subject because they are the person who
 *    acted; the target is the account they acted in. "On behalf of" implies the
 *    target asked for it, which the row cannot know.
 *  - A party the row does not name is said to be unnamed, never omitted. A
 *    half-length sentence would read as an ordinary one-person action, which is
 *    the failure this whole state exists to prevent.
 */
export function supportSessionActorPhrase(
  state: Extract<ActorState, { kind: 'support-session' }>,
): string {
  const operator = supportSessionParty(state.operator, true);
  const target = supportSessionParty(state.target, false);
  return `${operator}, acting as ${target},`;
}

/** Named because the sign-in sentence has to recognise it. */
const ANONYMOUS_ACTOR = 'Someone not signed in';

/** Classifies the status code. Anything unclassifiable is a failure, never a success. */
function outcomeOf(statusCode: unknown): ActivityOutcome {
  if (typeof statusCode !== 'number' || !Number.isFinite(statusCode)) return 'failed';
  if (statusCode >= 200 && statusCode < 300) return 'succeeded';
  if (statusCode === 401) return 'unauthenticated';
  if (statusCode === 403) return 'denied';
  if (statusCode === 404) return 'not-found';
  if (statusCode === 422) return 'invalid';
  if (statusCode === 429) return 'rate-limited';
  return 'failed';
}

/**
 * The label, with one refinement over a flat lookup.
 *
 * `failed` is the catch-all bucket: it holds real 5xx responses and also
 * anything unclassified (a 3xx, a missing status). Only an actual 5xx is
 * labelled "server error" — printing that beside a row whose status could not
 * be read would assert whose fault it was without knowing.
 */
function outcomeLabelOf(outcome: ActivityOutcome, statusCode: unknown): string {
  if (outcome === 'failed') {
    const isServerError = typeof statusCode === 'number' && statusCode >= 500 && statusCode < 600;
    return isServerError ? 'server error' : OUTCOME_LABELS.failed;
  }
  return OUTCOME_LABELS[outcome];
}

/** Action first, method second, "accessed" last. */
function verbFor(row: Partial<ApiLogSummary>): VerbForms {
  const action = text(row.action);
  if (action !== null && ACTION_SLUGS.includes(action)) {
    const byAction = ACTION_VERBS[action];
    if (byAction !== undefined) return byAction;
  }

  const method = text(row.method)?.toUpperCase();
  if (method !== undefined) {
    const byMethod = METHOD_VERBS[method];
    if (byMethod !== undefined) return byMethod;
  }

  return UNKNOWN_VERB;
}

/**
 * `module` + `recordId` as a noun phrase, or null when there is no module.
 *
 * The verb is needed because a missing `recordId` means two different things
 * depending on it. On a read it is a collection route — "viewed the deals list".
 * On a create it is a POST to that same collection URL, and "created the deals
 * list" is nonsense; the record being made simply has no id yet (or the request
 * failed before it got one), so it is named indefinitely: "created a deal".
 */
function objectFor(row: Partial<ApiLogSummary>, verb: VerbForms): string | null {
  const slug = text(row.module);
  if (slug === null) return null;

  const nouns = nounsFor(slug);
  // `recordId` is ALWAYS a string in this contract, even for numeric ids —
  // never compare it to 0 or do arithmetic on it. Null means no record in the URL.
  const id = text(row.recordId);
  if (id !== null) return `${nouns.singular} #${id}`;

  if (verb.infinitive === 'create' && nouns.known) {
    const noun = nouns.singular.toLowerCase();
    return `${indefiniteArticle(noun)} ${noun}`;
  }
  return nouns.collection;
}

/**
 * 'a' or 'an' by first letter.
 *
 * Crude on purpose. It runs only over the twelve singulars in `MODULE_NOUNS`,
 * where the letter rule is right every time; a general English solution would
 * be far more machinery than "an invoice, an intake" is worth.
 */
function indefiniteArticle(noun: string): string {
  return /^[aeiou]/.test(noun) ? 'an' : 'a';
}

/**
 * The in-app link, or null.
 *
 * Three separate reasons to return null, all of them "we would be guessing":
 * the module has no detail page in this app, the row is a collection route with
 * no record, or the id is not a plain URL segment. The last guard matters
 * because `recordId` is free-form text captured from a URL param — a slashy or
 * encoded value pasted straight into a path silently produces a different route.
 */
function hrefFor(row: Partial<ApiLogSummary>): string | null {
  const slug = text(row.module);
  if (slug === null) return null;

  const base = DETAIL_ROUTES[slug];
  if (base === undefined) return null;

  const id = text(row.recordId);
  if (id === null || !/^[A-Za-z0-9_-]+$/.test(id)) return null;

  return `${base}/${id}`;
}

/**
 * Marks the rows the feed hides by default.
 *
 * Deliberately conservative: the page shows a "N routine events hidden" count,
 * and something wrongly classed as noise is something nobody ever sees. Reads
 * and health checks are the bulk of the volume and none of the interest;
 * everything that changed state stays visible.
 */
function isRoutine(row: Partial<ApiLogSummary>): boolean {
  return text(row.method)?.toUpperCase() === 'GET' && text(row.action) === 'read';
}

/**
 * Assembles the sentence, and this is where rule 2 lives.
 *
 * Only a 2xx gets the past tense. Everything else is "tried to …", because a
 * 403 on a delete means the record is still there, and a feed that read
 * "deleted Quote #3" would have someone chasing a deletion that never happened.
 * No trailing full stop — the caller appends "· not permitted".
 */
function buildSentence(
  actor: string,
  verb: VerbForms,
  object: string | null,
  succeeded: boolean,
): string {
  const head = succeeded ? `${actor} ${verb.past}` : `${actor} tried to ${verb.infinitive}`;
  if (object !== null) return `${head} ${object}`;
  /*
   * No object. A completed action still reads ("Varun Khubani signed out"), but
   * an ATTEMPT does not — "tried to create" trails off mid-thought. Close it
   * rather than emitting half a sentence; the row's technical details carry the
   * specifics the wording cannot.
   */
  return succeeded ? head : `${head} something`;
}

/**
 * What a special-cased route produces.
 *
 * `sentence` is optional: most special cases only need their own verb and
 * object and can reuse the standard attempt/completion phrasing. Sign-in is the
 * exception and overrides the whole sentence.
 */
interface SpecialCase {
  verb: VerbForms;
  object: string | null;
  routine: boolean;
  sentence?: (actor: string, succeeded: boolean) => string;
}

const SIGN_IN: VerbForms = { label: 'signed in', past: 'signed in', infinitive: 'sign in' };
const SIGN_OUT: VerbForms = { label: 'signed out', past: 'signed out', infinitive: 'sign out' };
const RENEW_SESSION: VerbForms = {
  label: 'renewed their session',
  past: 'renewed their session',
  infinitive: 'renew their session',
};
const CHECK_ACCESS: VerbForms = {
  label: 'checked their own access',
  past: 'checked their own access',
  infinitive: 'check their own access',
};
const HEALTH_CHECK: VerbForms = {
  label: 'health check',
  past: 'ran a health check',
  infinitive: 'run a health check',
};
const CHANGE_GRANTS: VerbForms = { label: 'updated', past: 'updated', infinitive: 'update' };

/**
 * Routes that need their own wording, matched on the pattern rather than the
 * module — every one of these logs `module: null` because it sits outside the
 * permission system, so the generic path has nothing to work with and would
 * emit "Varun Khubani created" with no object at all.
 */
function specialCase(route: string, row: Partial<ApiLogSummary>): SpecialCase | null {
  if (route === '/auth/login') {
    return {
      verb: SIGN_IN,
      object: null,
      routine: false,
      /*
       * A failed sign-in is the single most security-relevant line in this log,
       * so it gets its own phrasing instead of the generic "tried to sign in".
       * The actor is almost always anonymous here — the request carries no token
       * yet — and "Someone not signed in tried to sign in" buries the one thing
       * worth noticing. The email that was attempted is NOT available to say:
       * the request body is redacted and is not on the summary row at all, so
       * the sentence reports that an attempt failed and nothing about whose.
       */
      sentence: (actor, succeeded) => {
        if (actor !== ANONYMOUS_ACTOR) {
          return succeeded ? `${actor} signed in` : `${actor} failed to sign in`;
        }
        // The request that creates a session cannot have carried one, so the
        // actor on this row is usually null — and "Someone not signed in signed
        // in" is both true and useless. State the event instead of the actor.
        return succeeded ? 'A successful sign-in' : 'A failed sign-in attempt';
      },
    };
  }

  if (route === '/auth/logout') {
    return { verb: SIGN_OUT, object: null, routine: false };
  }

  // Token renewal is machinery, not activity: the browser does it on a timer,
  // and unhidden it would be the single most common line in the feed.
  if (route === '/auth/refresh') {
    return { verb: RENEW_SESSION, object: null, routine: true };
  }

  if (route === '/auth/me' || route === '/permissions/me') {
    return { verb: CHECK_ACCESS, object: null, routine: true };
  }

  // '/health', '/health/live', '/health/ready' — whatever the probe hits.
  if (route === '/health' || route.startsWith('/health/')) {
    return { verb: HEALTH_CHECK, object: null, routine: true };
  }

  return permissionSetCase(route, row);
}

/**
 * The permission-set family, where the recorded `recordId` does not mean what
 * the URL suggests. Two documented quirks, handled rather than papered over:
 *
 *  - `/permission-sets/:id/items/:itemId` records the SET id, not the item id.
 *    Rendering it as "Permission set item #3" would point at the wrong row, so
 *    the object names the set and calls the item "an entry" — true, and no more
 *    precise than the data.
 *  - `/users/:userId/permission-sets` records `recordId: null`, because the
 *    param is `userId` and only `:id` is captured. We know a user's grants
 *    changed; we do not know whose. The sentence says exactly that much.
 *
 * No `href` from any of these. `/permission-sets/:id` does exist in the route
 * table, but it is super-admin-only in this app and the one reliable id here is
 * a SET id on rows that are about an item — so a link would as often as not
 * take the reader somewhere they cannot open, about something they did not
 * click. That is worse than no link.
 */
function permissionSetCase(route: string, row: Partial<ApiLogSummary>): SpecialCase | null {
  if (/^\/users\/[^/]+\/permission-sets$/.test(route)) {
    return { verb: CHANGE_GRANTS, object: "a user's permission sets", routine: false };
  }

  if (!route.startsWith('/permission-sets')) return null;

  const id = text(row.recordId);
  const set = id === null ? 'a permission set' : `Permission set #${id}`;

  if (/\/items\/[^/]+$/.test(route)) {
    return { verb: verbFor(row), object: `an entry in ${set}`, routine: false };
  }

  if (route === '/permission-sets') {
    return {
      verb: verbFor(row),
      object: id === null ? 'the permission sets list' : set,
      routine: false,
    };
  }

  return { verb: verbFor(row), object: set, routine: false };
}
